/* eslint-disable no-underscore-dangle */
import {
  QueryResult,
  OperationVariables,
  DocumentNode,
  gql,
} from "@apollo/client";
import { NextRouter, useRouter } from "next/router";
import { publicListingNiceSearch_publicListingNiceSearch_listings_nodes } from "components/ClientSearch/Listings/types/publicListingNiceSearch";
import DOMPurify from "isomorphic-dompurify";
import {
  areaQueryWithCmsContent_area_Market_cmsContent,
  areaQueryWithCmsContent_area_Submarket,
  areaQueryWithCmsContent_area_Neighborhood,
  areaQueryWithCmsContent_area_Market_cmsContent_faq,
} from "types/pages/office-space/m/areaQueryWithCmsContent";
import { areaQueryWithSeoString } from "util/seo/types/areaQueryWithSeoString";
import { BuildingPageQuery_buildingOrRedirect_building } from "types/pages/building/BuildingPageQuery";
import jsonld from "util/jsonld";
import { parameterize, titleize } from "util/strings";

export function sanitizeCMS(
  cmsContent: areaQueryWithCmsContent_area_Market_cmsContent
) {
  const { title, content } = cmsContent;
  return {
    ...cmsContent,
    title: title ? DOMPurify.sanitize(title) : "",
    content: content ? DOMPurify.sanitize(content) : "",
  };
}

export const AREA_QUERY_WITH_SEO_STRING = gql`
  query areaQueryWithSeoString($externalId: ID!) {
    area(externalId: $externalId) {
      id
      externalId
      seoString
      name
      __typename
    }
  }
`;

export async function getAreaSeoData(
  apolloClient: {
    query: (arg0: {
      query: DocumentNode;
      variables: { externalId: string };
    }) =>
      | QueryResult<areaQueryWithSeoString, OperationVariables>
      | PromiseLike<QueryResult<areaQueryWithSeoString, OperationVariables>>;
  },
  externalId: string
) {
  const { data: areaSeoData, error }: QueryResult<areaQueryWithSeoString> =
    await apolloClient.query({
      query: AREA_QUERY_WITH_SEO_STRING,
      variables: { externalId },
    });

  if (error) throw error;
  return areaSeoData;
}

export function getAreaSeoRedirect(
  areaSeoData: areaQueryWithSeoString,
  resolvedUrl: string
) {
  if (!areaSeoData?.area) return { notFound: true };

  const { externalId: returnedExternalId, seoString } = areaSeoData.area;

  if (seoString) {
    const idealUrl = `/office-space/m/${seoString}/${returnedExternalId}`;
    const [path, queryParams] = resolvedUrl.split("?");
    if (idealUrl !== path) {
      const destination = queryParams ? `${idealUrl}?${queryParams}` : idealUrl;
      return {
        redirect: {
          destination,
          permanent: true,
        },
      };
    }
  }
  return null;
}

export const getSearchPageMetaData = (
  market: {
    name: string;
    externalId: string;
    seoString: string;
  } | null,
  listings: publicListingNiceSearch_publicListingNiceSearch_listings_nodes[],
  areasOfInterest: (
    | areaQueryWithCmsContent_area_Neighborhood
    | areaQueryWithCmsContent_area_Submarket
  )[] // replace type with generated type.
  // faqs: { question: string; answer: string }[]
) => {
  const breadcrumbItems = [];
  if (market)
    breadcrumbItems.push({
      "@type": "ListItem",
      position: 1,
      item: {
        "@id": `https://www.squarefoot.com/office-space/m/${market.seoString}/${market.externalId}`,
        name: titleize(market.name),
      },
    });
  const areaOfInterest = areasOfInterest[0] || false;
  if (areaOfInterest) {
    if (areaOfInterest.__typename === "Submarket") {
      breadcrumbItems.push({
        "@type": "ListItem",
        position: 2,
        item: {
          "@id": `https://www.squarefoot.com/office-space/ice/${areaOfInterest.seoString}/${areaOfInterest.externalId}`,
          name: `${titleize(areaOfInterest.name)}, ${titleize(
            market?.name || ""
          )}`,
        },
      });
    }
    if (areaOfInterest.__typename === "Neighborhood") {
      const submarket = areaOfInterest?.submarketsOfNhood?.[0];
      if (submarket) {
        breadcrumbItems.push({
          "@type": "ListItem",
          position: 2,
          item: {
            "@id": `https://www.squarefoot.com/office-space/${submarket.seoString}/${submarket.externalId}`,
            name: `${titleize(submarket.name)}, ${titleize(
              market?.name || ""
            )}`,
          },
        });
        breadcrumbItems.push({
          "@type": "ListItem",
          position: 3,
          item: {
            "@id": `https://www.squarefoot.com/office-space/m/${areaOfInterest.seoString}/${areaOfInterest.externalId}`,
            name: `${titleize(areaOfInterest.name)}, ${titleize(
              submarket.name
            )}, ${titleize(market?.name || "")}`,
          },
        });
      }
    }
  }
  const metadata = [
    jsonld("BreadcrumbList", { itemListElement: breadcrumbItems }),
  ];

  listings.forEach(
    ({
      id,
      building,
      coverImage,
      floorAndSuite,
      estimatedPsf,
      accuratePsf,
      leaseType,
    }) => {
      metadata.push(
        jsonld("Product", {
          name: building.address,
          category: "Office Space", // spaceType
          image: coverImage?.url,
          offers: [
            {
              "@type": "Offer",
              name: `${building.address} ${floorAndSuite}`,
              url: `/building${building.slug}/${building.identity}?listingId=${id}`,
              price: accuratePsf || estimatedPsf || 0,
              priceCurrency: "USD",
              category:
                leaseType === "LICENSE_AGREEMENT"
                  ? "Co Working"
                  : titleize(leaseType || ""),
              availableAtOrFrom: {
                "@type": "Place",
                address: {
                  "@type": "PostalAddress",
                  streetAddress: building.address,
                  addressLocality: building.city,
                  addressRegion: building.state,
                  postalCode: building.zip,
                },
              },
            },
          ],
        })
      );
    }
  );

  return metadata;
};

export const getFaqMetaData = (
  faqs: areaQueryWithCmsContent_area_Market_cmsContent_faq[] | null
) => {
  if (!faqs) return [];
  return jsonld("FAQPage", {
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  });
};

export const generateSearchParams = (index: number = 1, router: NextRouter) => {
  let search;
  if (typeof URLSearchParams !== "undefined") {
    search = new URLSearchParams(router.asPath.split("?")?.[1]);
    search.set("page", index.toString());
    search = search.toString();
  } else {
    search = `page=${index}`;
  }
  return search;
};

export const GetMetaDataLinks = (page: number) => {
  const router = useRouter();
  const prevPage = page - 1;
  const nextPage = page + 1;
  const nextParam = `?${generateSearchParams(nextPage, router)}`;
  const previousParam =
    prevPage < 2 ? "" : `?${generateSearchParams(prevPage, router)}`;
  const canonicalParam = page >= 2 ? `?page=${page}` : "";
  const host =
    typeof window !== "undefined" ? `https://${window.location.hostname}` : "";
  const baseLink = `${host}${router.asPath.split("?")[0]}`;
  return {
    canonical: `${baseLink}${canonicalParam}`,
    previous: `${baseLink}${previousParam}`,
    next: `${baseLink}${nextParam}`,
  };
};

export const getMarketLink = (
  building: BuildingPageQuery_buildingOrRedirect_building
) =>
  building.market
    ? `/office-space/m/${
        building.market.seoString ||
        `${parameterize(building.state)}/${parameterize(building.city)}` // use state city if seo string is falsy
      }/${building.market.externalId}`
    : `/office-space/${parameterize(building.state)}/${parameterize(
        building.city
      )}`;

export const getSubmarketLink = (
  building: BuildingPageQuery_buildingOrRedirect_building
) =>
  `/office-space/m/${building.submarket?.seoString}/${building.submarket?.externalId}`;

export const getNeighborhoodLink = (
  building: BuildingPageQuery_buildingOrRedirect_building
) =>
  building.market
    ? `/office-space/m${building.neighborhood?.rootSlug}/${building.neighborhood?.externalId}`
    : `/office-space${building.neighborhood?.rootSlug}`;

export const getBuildingMetaDescription = (
  building: BuildingPageQuery_buildingOrRedirect_building
) =>
  building.listings?.length
    ? `There are ${building.listings.length} office space listings at ${building.address}. Find the perfect space for your business at SquareFoot`
    : `View office space availabilities at ${building.address}. Find the perfect space for your business at SquareFoot`;

export const getBuildingMetaData = (
  building: BuildingPageQuery_buildingOrRedirect_building,
  isListingSelected: boolean
) => {
  const marketLink = getMarketLink(building);
  const submarketLink = getSubmarketLink(building);
  const neighborhoodLink = getNeighborhoodLink(building);
  const breadcrumbItems = [
    {
      "@type": "ListItem",
      item: {
        "@id": `https://www.squarefoot.com${marketLink}`,
        name: titleize(building.market?.name || building.city),
      },
    },
  ];
  if (building.submarket)
    breadcrumbItems.push({
      "@type": "ListItem",
      item: {
        "@id": `https://www.squarefoot.com${submarketLink}`,
        name: titleize(building.submarket?.name || ""),
      },
    });
  if (building.neighborhood)
    breadcrumbItems.push({
      "@type": "ListItem",
      item: {
        "@id": `https://www.squarefoot.com${neighborhoodLink}`,
        name: titleize(building.neighborhood?.name || ""),
      },
    });

  if (isListingSelected) {
    breadcrumbItems.push({
      "@type": "ListItem",
      item: {
        "@id": `https://www.squarefoot.com/building${building.slug}/${building.identity}`,
        name: titleize(building.address || ""),
      },
    });
  }

  const meta = [
    jsonld("BreadcrumbList", {
      itemListElement: breadcrumbItems.map((item, i) => ({
        ...item,
        position: i + 1,
      })),
    }),
    jsonld("Place", {
      address: jsonld("PostalAddress", {
        addressCountry: "USA",
        streetAddress: building.address,
        addressLocality: building.city,
        addressRegion: building.state,
        postalCode: building.zip,
        areaServed: building.neighborhood
          ? building.neighborhood?.name
          : building.city,
      }),
      geo: jsonld("GeoCoordinates", {
        latitude: building.lat,
        longitude: building.lng,
      }),
      image: building.coverImage.url,
      potentialAction: jsonld("RentAction", {
        realEstateAgent: jsonld("RealEstateAgent", {
          name: "SquareFoot",
        }),
      }),
    }),
  ];

  return JSON.stringify(meta);
};
